import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  DatePicker,
} from 'antd';
import { format } from 'date-fns';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import FeeScheduleResponse from '../../model/Fee-schedule.model';
import FeeScheduleService from '../../service/fee-schedule-service/Fee-schedule.service';
import CreateFeeScheduleModal from './CreateFeeScheduleModal';
import EditFeeScheduleModal from './EditFeeScheduleModal';
import FeeScheduleHistoryModal from './FeeScheduleHistoryModal';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

const FeeScheduleList: React.FC = () => {
  const [feeSchedules, setFeeSchedules] = useState<FeeScheduleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeeSchedule, setSelectedFeeSchedule] =
    useState<FeeScheduleResponse | null>(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(9);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedHistoryFeeSchedule, setSelectedHistoryFeeSchedule] =
    useState<FeeScheduleResponse | null>(null);

  // Thêm state cho các filter
  const [bankFilter, setBankFilter] = useState<string[]>([]);
  const [feeRateFilter, setFeeRateFilter] = useState<string[]>([]);
  const [feeTypeFilter, setFeeTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [posDeviceFilter, setPosDeviceFilter] = useState<string[]>([]);
  const [cardTypeFilter, setCardTypeFilter] = useState<string[]>([]);
  const [effectiveDateFilter, setEffectiveDateFilter] = useState<string[]>([]);

  // Tạo danh sách filter options
  const bankOptions = useMemo(() => {
    const banks = new Set<string>();
    feeSchedules.forEach((fs) => {
      if (fs.bank?.name) {
        banks.add(fs.bank.name);
      } else {
        banks.add('-');
      }
    });
    return Array.from(banks).map((bank) => ({ text: bank, value: bank }));
  }, [feeSchedules]);

  const posDeviceOptions = useMemo(() => {
    const devices = new Set<string>();
    feeSchedules.forEach((fs) => {
      if (fs.posDevice?.deviceCode) {
        devices.add(fs.posDevice.deviceCode);
      } else {
        devices.add('-');
      }
    });
    return Array.from(devices).map((device) => ({
      text: device,
      value: device,
    }));
  }, [feeSchedules]);

  const cardTypeOptions = useMemo(() => {
    const types = new Set<string>();
    feeSchedules.forEach((fs) => {
      if (fs.cardType?.name) {
        types.add(fs.cardType.name);
      } else {
        types.add('-');
      }
    });
    return Array.from(types).map((type) => ({ text: type, value: type }));
  }, [feeSchedules]);

  const feeRateOptions = useMemo(() => {
    const rates = new Set<string>();
    feeSchedules.forEach((fs) => {
      rates.add(`${fs.feeRate}%`);
    });
    return Array.from(rates).map((rate) => ({ text: rate, value: rate }));
  }, [feeSchedules]);

  const feeTypeOptions = useMemo(
    () => [
      { text: 'Mặc định', value: 'default' },
      { text: 'Đặc thù', value: 'special' },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { text: 'Hoạt động', value: 'active' },
      { text: 'Không hoạt động', value: 'inactive' },
    ],
    []
  );

  const fetchFeeSchedules = async (search: string = '') => {
    setLoading(true);
    try {
      let data: FeeScheduleResponse[];
      if (activeTab === 'deleted') {
        const res = await FeeScheduleService.getDeletedFeeSchedules();
        data = Array.isArray(res) ? res : res.content || [];
      } else {
        data = await FeeScheduleService.getAllFeeSchedules();
      }
      // Nếu có search term, lọc trên toàn bộ dữ liệu
      if (search.trim()) {
        const searchTermLower = search.toLowerCase();
        const filteredContent = data.filter((feeSchedule) => {
          const posDeviceCode =
            feeSchedule.posDevice?.deviceCode?.toLowerCase() || '';
          const cardTypeName = feeSchedule.cardType?.name?.toLowerCase() || '';
          const bankName = feeSchedule.bank?.name?.toLowerCase() || '';
          return (
            posDeviceCode.includes(searchTermLower) ||
            cardTypeName.includes(searchTermLower) ||
            bankName.includes(searchTermLower)
          );
        });
        setFeeSchedules(filteredContent);
      } else {
        setFeeSchedules(data);
      }
    } catch (error: any) {
      console.error('Error fetching fee schedules:', error);
      toast.error(error.message || 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (feeSchedule: FeeScheduleResponse) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content:
        'Bạn có chắc chắn muốn xóa biểu phí này không? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await FeeScheduleService.softDeleteFeeSchedule(feeSchedule.id);
          const newPage =
            feeSchedules.length === 1 && page > 0 ? page - 1 : page;
          setPage(newPage);
          await fetchFeeSchedules(debouncedSearchTerm);
          toast.success('Xóa biểu phí thành công');
        } catch (error: any) {
          console.error('Error deleting fee schedule:', error);
          toast.error(error.message || 'Có lỗi xảy ra khi xóa biểu phí');
        }
      },
    });
  };

  useEffect(() => {
    fetchFeeSchedules(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Lọc dữ liệu dựa trên các filter
  const filteredFeeSchedules = useMemo(() => {
    let filtered = feeSchedules;

    // Lọc theo search term
    if (debouncedSearchTerm.trim()) {
      const searchTermLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((feeSchedule) => {
        const posDeviceCode =
          feeSchedule.posDevice?.deviceCode?.toLowerCase() || '';
        const cardTypeName = feeSchedule.cardType?.name?.toLowerCase() || '';
        const bankName = feeSchedule.bank?.name?.toLowerCase() || '';

        return (
          posDeviceCode.includes(searchTermLower) ||
          cardTypeName.includes(searchTermLower) ||
          bankName.includes(searchTermLower)
        );
      });
    }

    // Lọc theo ngân hàng
    if (bankFilter.length > 0) {
      filtered = filtered.filter((fs) => {
        const bankName = fs.bank?.name || '-';
        return bankFilter.includes(bankName);
      });
    }

    // Lọc theo mức phí
    if (feeRateFilter.length > 0) {
      filtered = filtered.filter((fs) => {
        return feeRateFilter.includes(`${fs.feeRate}%`);
      });
    }

    // Lọc theo loại biểu phí
    if (feeTypeFilter.length > 0) {
      filtered = filtered.filter((fs) => {
        const feeType = fs.bank ? 'special' : 'default';
        return feeTypeFilter.includes(feeType);
      });
    }

    // Lọc theo trạng thái
    if (statusFilter.length > 0) {
      filtered = filtered.filter((fs) => {
        const status = fs.isActive ? 'active' : 'inactive';
        return statusFilter.includes(status);
      });
    }

    return filtered;
  }, [
    feeSchedules,
    debouncedSearchTerm,
    bankFilter,
    feeRateFilter,
    feeTypeFilter,
    statusFilter,
  ]);

  // Reset tất cả filter
  const resetAllFilters = () => {
    setBankFilter([]);
    setFeeRateFilter([]);
    setFeeTypeFilter([]);
    setStatusFilter([]);
    setPosDeviceFilter([]);
    setCardTypeFilter([]);
    setEffectiveDateFilter([]);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  // Kiểm tra có filter nào đang active không
  const hasActiveFilters = !!(
    bankFilter.length > 0 ||
    feeRateFilter.length > 0 ||
    feeTypeFilter.length > 0 ||
    statusFilter.length > 0 ||
    posDeviceFilter.length > 0 ||
    cardTypeFilter.length > 0 ||
    effectiveDateFilter.length > 0 ||
    debouncedSearchTerm.trim()
  );

  // Xử lý filter change của Table
  const handleTableChange = (_: any, filters: Record<string, any>) => {
    console.log('Filter change:', filters);

    if (filters.hasOwnProperty('bank')) {
      setBankFilter(filters.bank || []);
    }
    if (filters.hasOwnProperty('posDevice')) {
      setPosDeviceFilter(filters.posDevice || []);
    }
    if (filters.hasOwnProperty('cardType')) {
      setCardTypeFilter(filters.cardType || []);
    }
    if (filters.hasOwnProperty('effectiveDate')) {
      setEffectiveDateFilter(filters.effectiveDate || []);
    }
    if (filters.hasOwnProperty('feeRate')) {
      setFeeRateFilter(filters.feeRate || []);
    }
    if (filters.hasOwnProperty('feeType')) {
      setFeeTypeFilter(filters.feeType || []);
    }
    if (filters.hasOwnProperty('isActive')) {
      setStatusFilter(filters.isActive || []);
    }
  };

  const columns = [
    {
      title: 'Thiết bị POS',
      dataIndex: ['posDevice', 'deviceCode'],
      key: 'posDevice',
      width: 150,
      render: (text: string, record: FeeScheduleResponse) => (
        <div>
          <div className="font-medium">{text || 'Chưa gán thiết bị'}</div>
          {record.posDevice && (
            <div className="text-xs text-gray-500">
              {record.posDevice.location}
            </div>
          )}
        </div>
      ),
      filters: posDeviceOptions,
      filterMultiple: true,
      filteredValue: posDeviceFilter.length > 0 ? posDeviceFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        const deviceCode = record.posDevice?.deviceCode || '-';
        return deviceCode === value;
      },
    },
    {
      title: 'Loại thẻ',
      dataIndex: ['cardType', 'name'],
      key: 'cardType',
      width: 150,
      render: (text: string, record: FeeScheduleResponse) => (
        <div>
          <div className="font-medium">{text}</div>
        </div>
      ),
      filters: cardTypeOptions,
      filterMultiple: true,
      filteredValue: cardTypeFilter.length > 0 ? cardTypeFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        const cardTypeName = record.cardType?.name || '-';
        return cardTypeName === value;
      },
    },
    {
      title: 'Ngân hàng',
      dataIndex: ['bank', 'name'],
      key: 'bank',
      width: 150,
      filters: bankOptions,
      filterMultiple: true,
      filteredValue: bankFilter.length > 0 ? bankFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        const bankName = record.bank?.name || '-';
        return bankName === value;
      },
      filterIcon: (filtered: boolean) => (
        <FilterOutlined className={filtered ? 'text-blue-500' : undefined} />
      ),
      render: (text: string, record: FeeScheduleResponse) => (
        <div>
          <div className="font-medium">{text ? text : '-'}</div>
        </div>
      ),
    },
    {
      title: 'Mức phí',
      dataIndex: 'feeRate',
      key: 'feeRate',
      width: 100,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Nhập mức phí..."
            value={selectedKeys[0] || ''}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 120, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterOutlined className={filtered ? 'text-blue-500' : undefined} />
      ),
      filteredValue: feeRateFilter.length > 0 ? feeRateFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        if (!value || value.length === 0) return true;
        return record.feeRate.toString().includes(value.toString());
      },
      render: (feeRate: number) => (
        <Tag color="blue" className="font-medium">
          {feeRate}%
        </Tag>
      ),
    },
    {
      title: 'Loại biểu phí',
      key: 'feeType',
      width: 120,
      filters: feeTypeOptions,
      filterMultiple: true,
      filteredValue: feeTypeFilter.length > 0 ? feeTypeFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        const feeType = record.bank ? 'special' : 'default';
        return feeType === value;
      },
      filterIcon: (filtered: boolean) => (
        <FilterOutlined className={filtered ? 'text-blue-500' : undefined} />
      ),
      render: (_: any, record: FeeScheduleResponse) => (
        <Tag color={record.bank ? 'purple' : 'green'} className="font-medium">
          {record.bank ? 'Đặc thù' : 'Mặc định'}
        </Tag>
      ),
    },
    {
      title: 'Ngày hiệu lực',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      width: 120,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            format="DD-MM-YYYY"
            value={
              selectedKeys[0] ? dayjs(selectedKeys[0], 'YYYY-MM-DD') : null
            }
            onChange={(date) =>
              setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : [])
            }
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterOutlined className={filtered ? 'text-blue-500' : undefined} />
      ),
      filteredValue:
        effectiveDateFilter.length > 0 ? effectiveDateFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        if (!value) return true;
        return record.effectiveDate === value;
      },
      render: (date: string) => (
        <span className="font-medium">
          {format(new Date(date), 'dd-MM-yyyy')}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      filters: statusOptions,
      filterMultiple: true,
      filteredValue: statusFilter.length > 0 ? statusFilter : null,
      onFilter: (value: any, record: FeeScheduleResponse) => {
        const status = record.isActive ? 'active' : 'inactive';
        return status === value;
      },
      filterIcon: (filtered: boolean) => (
        <FilterOutlined className={filtered ? 'text-blue-500' : undefined} />
      ),
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'error'} className="font-medium">
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Ngày xóa',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 120,
      render: (date: string) =>
        date ? (
          <span className="font-medium">
            {format(new Date(date), 'dd-MM-yyyy HH:mm')}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
      hidden: activeTab !== 'deleted',
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: FeeScheduleResponse) => (
        <Space size="small">
          {activeTab === 'active' && (
            <>
              <Button
                type="text"
                onClick={() => setSelectedFeeSchedule(record)}
                icon={<EditOutlined />}
                title="Chỉnh sửa"
                className="text-blue-500 hover:text-blue-700"
              />
              <Button
                type="text"
                danger
                onClick={() => handleDelete(record)}
                icon={<DeleteOutlined />}
                title="Xóa"
                className="text-red-500 hover:text-red-700"
              />
            </>
          )}
        </Space>
      ),
    },
  ].filter((column) => !column.hidden);

  // fetchAllFeeSchedules cho modal
  const fetchAllFeeSchedules = async () => {
    return await FeeScheduleService.getAllFeeSchedules();
  };

  return (
    <div className="w-full">
      <Tabs activeKey={activeTab} onChange={setActiveTab} className="mb-4">
        <TabPane tab="Biểu phí đang hoạt động" key="active" />
        <TabPane tab="Biểu phí đã xóa" key="deleted" />
      </Tabs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <Space className="w-full sm:w-auto">
          {activeTab === 'active' && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalOpen(true)}
                className="w-full sm:w-auto"
              >
                Tạo biểu phí mới
              </Button>
              {/* <ExportFeeScheduleButton /> */}
            </>
          )}
          {hasActiveFilters && (
            <Button
              onClick={resetAllFilters}
              type="default"
              size="middle"
              className="bg-gray-100 hover:bg-gray-200"
            >
              Xóa tất cả bộ lọc
            </Button>
          )}
        </Space>
        {activeTab === 'active' && (
          <Input
            placeholder="Tìm kiếm theo mã thiết bị hoặc loại thẻ"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-80"
            allowClear
          />
        )}
        {activeTab === 'deleted' && (
          <Input
            placeholder="Tìm kiếm theo mã thiết bị hoặc loại thẻ"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-80"
            allowClear
          />
        )}
      </div>

      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={feeSchedules}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page + 1,
            pageSize: size,
            total: totalElements,
            onChange: (newPage) => {
              setPage(newPage - 1);
            },
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} biểu phí`,
            showSizeChanger: false,
            hideOnSinglePage: false,
          }}
          scroll={{ x: 800 }}
          size="middle"
          className="bg-white rounded-lg shadow"
          onChange={handleTableChange}
        />
      </div>

      <CreateFeeScheduleModal
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onRefresh={() => fetchFeeSchedules(debouncedSearchTerm)}
        fetchAllFeeSchedules={fetchAllFeeSchedules}
      />

      {selectedFeeSchedule && (
        <EditFeeScheduleModal
          visible={!!selectedFeeSchedule}
          onClose={() => setSelectedFeeSchedule(null)}
          feeSchedule={selectedFeeSchedule}
          onSuccess={() => {
            fetchFeeSchedules(debouncedSearchTerm);
            setSelectedFeeSchedule(null);
          }}
        />
      )}

      {selectedHistoryFeeSchedule && (
        <FeeScheduleHistoryModal
          visible={historyModalVisible}
          onClose={() => {
            setHistoryModalVisible(false);
            setSelectedHistoryFeeSchedule(null);
          }}
          posDeviceId={selectedHistoryFeeSchedule.posDevice?.posId || 0}
          cardTypeId={selectedHistoryFeeSchedule.cardType.cardTypeId}
          cardTypeName={selectedHistoryFeeSchedule.cardType.name}
          bankId={selectedHistoryFeeSchedule.bank?.id || null}
          bankName={selectedHistoryFeeSchedule.bank?.name || ''}
        />
      )}
    </div>
  );
};

export default FeeScheduleList;
